import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Clock, DollarSign, Users, ExternalLink, Coins, TrendingUp, Info, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useAccount, useReadContract } from 'wagmi';
import { formatUnits } from 'viem';
import toast from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/api';
import { debugLog, debugWarn, debugError } from '../utils/debug';
import { useOpenUrl } from '@coinbase/onchainkit/minikit';
import './CoinModal.css';

const CoinModal = ({ isOpen, onClose, confessionId }) => {
  const [coinData, setCoinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmittingPurchase, setIsSubmittingPurchase] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [checkingToken, setCheckingToken] = useState(false);
  const [showReportSection, setShowReportSection] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  
  const { address, isConnected } = useAccount();
  
  // Use MiniKit hook for external URL handling
  const openUrl = useOpenUrl();
  
  // Detect MiniApp mode
  const isMiniApp = window?.parent !== window || 
                   navigator.userAgent.includes('MiniApp') ||
                   window.location.search.includes('miniapp=true');

  // Accessibility: Hide background content and trap focus when modal is open
  useEffect(() => {
    if (isOpen) {
      // Hide background content from screen readers
      const appElement = document.getElementById('root');
      if (appElement) {
        appElement.setAttribute('aria-hidden', 'true');
      }
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Focus the modal
      const modal = document.querySelector('.coin-modal');
      if (modal) {
        modal.focus();
      }
    } else {
      // Restore background content accessibility
      const appElement = document.getElementById('root');
      if (appElement) {
        appElement.removeAttribute('aria-hidden');
      }
      
      // Restore body scroll
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      const appElement = document.getElementById('root');
      if (appElement) {
        appElement.removeAttribute('aria-hidden');
      }
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && confessionId) {
      fetchCoinData();
    }
  }, [isOpen, confessionId]);

  const fetchCoinData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiBaseUrl()}/testimonies/${confessionId}`);
      if (!response.ok) throw new Error('Failed to fetch coin data');
      
      const data = await response.json();
      setCoinData(data);
      
      // Check token ownership when data loads
      if (isConnected && address && data.nftContractAddress) {
        checkTokenOwnership(data.nftContractAddress);
      }
    } catch (error) {
      debugError('Error fetching coin data:', error);
      toast.error('Failed to load coin data');
    } finally {
      setLoading(false);
    }
  };

  const checkTokenOwnership = async (contractAddress) => {
    if (!isConnected || !address || !contractAddress) {
      setHasToken(false);
      return;
    }

    // Skip check for debug/invalid contract addresses
    if (contractAddress.includes('debug') || contractAddress === 'disabled_for_debugging') {
      setHasToken(false);
      setCheckingToken(false);
      return;
    }

    try {
      setCheckingToken(true);
      debugLog('🔍 Checking ERC20 token balance on Base Mainnet:', { 
        walletAddress: address, 
        contractAddress: contractAddress
      });
      
      // Real ERC20 balance check via Base Mainnet API
      try {
        const apiUrl = `https://base.blockscout.com/api/v2/addresses/${address}/token-balances`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          debugLog('📊 Blockscout token balances:', data);
          
          // Check if wallet has any balance for this specific contract
          const hasBalance = data.some(token => 
            token.token && 
            token.token.address && 
            token.token.address.toLowerCase() === contractAddress.toLowerCase() &&
            parseFloat(token.value) > 0
          );
          
          setHasToken(hasBalance);
          setCheckingToken(false);
          
          if (hasBalance) {
            toast.success('✅ Token found in wallet! Full audio unlocked.');
          } else {
            toast('ℹ️ No tokens found for this confession. Preview mode only.');
          }
        } else {
          throw new Error(`API responded with ${response.status}`);
        }
      } catch (apiError) {
        debugWarn('⚠️ Base Mainnet Blockscout API failed, using fallback check:', apiError);
        
        // Fallback: Direct contract call using wagmi/viem
        // For now, default to no tokens (conservative approach)
        setHasToken(false);
        setCheckingToken(false);
        toast('ℹ️ Token check unavailable. Preview mode only.');
      }
      
    } catch (error) {
      debugError('❌ Error checking token ownership:', error);
      setHasToken(false);
      setCheckingToken(false);
      toast.error('Failed to check token ownership');
    }
  };

  const handleCheckZora = () => {
    if (!coinData?.nftContractAddress) return;
    
    const platformWallet = process.env.REACT_APP_PLATFORM_ADDRESS;
    const zoraUrl = `https://zora.co/coin/base:${coinData.nftContractAddress}?referrer=${platformWallet}`;
    
    console.log('🔍 CoinModal Debug info:', {
      isMiniApp,
      hasOpenUrl: !!openUrl,
      zoraUrl,
      userAgent: navigator.userAgent.substring(0, 100)
    });
    
    if (isMiniApp && openUrl) {
      openUrl(zoraUrl);
      toast.success('Opening Zora marketplace!');
    } else {
      window.open(zoraUrl, '_blank', 'noopener,noreferrer');
      toast.success('Opening Zora marketplace!');
    }
  };

  const formatPrice = (priceWei) => {
    if (!priceWei) return '0';
    return formatUnits(priceWei, 6);
  };

  const formatTimeRemaining = (endTime) => {
    if (!endTime) return 'Not started';
    
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;
    
    if (diff <= 0) return 'Auction ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const reportReasons = [
    'Hate Speech',
    'Illegal Content',
    'Harassment',
    'Spam',
    'Violence',
    'Inappropriate Content',
    'Other'
  ];

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    
    if (!reportReason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    if (!coinData?.id) {
      toast.error('Invalid confession data');
      return;
    }

    setIsSubmittingReport(true);

    try {
      debugLog('📋 Submitting report for confession:', coinData.id);
      
      const response = await fetch(`${getApiBaseUrl()}/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          confessionId: coinData.id,
          reason: reportReason,
          details: reportDetails.trim() || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit report');
      }

      debugLog('✅ Report submitted successfully:', data.reportId);
      
      toast.success('Report submitted successfully. Thank you for helping keep our community safe.');
      
      // Reset form and close section
      setReportReason('');
      setReportDetails('');
      setShowReportSection(false);
      
    } catch (error) {
      debugError('❌ Error submitting report:', error);
      toast.error(error.message || 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="coin-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="coin-modal"
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 50 }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          tabIndex={-1}
        >
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <X size={24} aria-hidden="true" />
          </button>

          {loading ? (
            <div className="modal-loading">
              <div className="spinner large"></div>
              <p>Loading coin data...</p>
            </div>
          ) : coinData ? (
            <div className="coin-content">
              <div className="coin-header">
                <h2 id="modal-title">
                  <Coins size={24} aria-hidden="true" />
                  Confession Coin
                </h2>
                <div className="coin-info">
                  <span className="coin-status">🪙 ERC20 Token</span>
                  <span className="chain-badge">Base</span>
                </div>
              </div>

              <div className="coin-body">
                <div className="coin-info">
                  <div className="testimony-content">
                    <h3>Confession</h3>
                    <p>{coinData.textContent}</p>
                  </div>

                  <div className="media-section">
                    <div className="media-info">
                      <Clock size={16} />
                      <span>{coinData.audioDuration}s</span>
                      {coinData.hasUnlockableContent && (
                        <span className={`unlockable-badge ${hasToken ? 'unlocked' : 'locked'}`}>
                          {hasToken ? '🔓 Full audio unlocked' : '🔒 Preview only'}
                        </span>
                      )}
                      
                      {!coinData.hasUnlockableContent && (
                        <span className="unlockable-badge free">
                          🔓 No token required
                        </span>
                      )}
                      
                      {checkingToken && (
                        <div className="token-check-status">
                          <div className="spinner small"></div>
                          <span>Checking wallet...</span>
                        </div>
                      )}
                      
                      {!checkingToken && isConnected && coinData.hasUnlockableContent && (
                        <div className={`token-status ${hasToken ? 'has-token' : 'no-token'}`}>
                          {hasToken ? (
                            <><CheckCircle size={14} /> Token owned</>
                          ) : (
                            <><XCircle size={14} /> No tokens</>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <video 
                      controls 
                      className="waveform-player" 
                      poster={coinData.imageUrl}
                      preload="metadata"
                      playsInline
                      onEnded={(e) => {
                        // Reset video to show poster again
                        e.target.load();
                      }}
                      key={`${coinData.id}-${hasToken ? 'full' : 'preview'}`} // Force re-render on token status change
                    >
                      <source src={
                        hasToken && coinData.hasUnlockableContent 
                          ? coinData.fullAudioUrl
                          : coinData.previewAudioUrl
                      } type="video/webm" />
                      Your browser does not support the video element.
                    </video>
                    
                  </div>

                  <div className="supported-ngos">
                    <h4>Supporting Charities:</h4>
                    <div className="ngo-list">
                      {coinData.ngos && coinData.ngos.map(ngo => (
                        <div key={ngo.id} className="ngo-item">
                          <Heart size={14} />
                          <span>{ngo.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="trading-section">
                  <div className="trading-header">
                    <h3>
                      <TrendingUp size={20} />
                      Coin Trading
                    </h3>
                    <div className="trading-status active">Live on Zora</div>
                  </div>

                  <div className="coin-stats">
                    <div className="stat-item">
                      <Info size={16} />
                      <div>
                        <label>Type</label>
                        <span>ERC20 Token</span>
                      </div>
                    </div>
                    <div className="stat-item">
                      <Coins size={16} />
                      <div>
                        <label>Network</label>
                        <span>Base</span>
                      </div>
                    </div>
                  </div>

                  <div className="purchase-section">
                    <motion.button
                      className="btn btn-primary btn-large"
                      onClick={handleCheckZora}
                      disabled={isSubmittingPurchase}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isSubmittingPurchase ? (
                        <>
                          <div className="spinner small"></div>
                          Opening Zora...
                        </>
                      ) : (
                        <>
                          🪙 Check on Zora
                        </>
                      )}
                    </motion.button>
                    
                    
                    {!isConnected && (
                      <p className="wallet-warning">
                        💡 Connect your wallet to check token ownership
                      </p>
                    )}
                  </div>

                  <div className="external-links">
                    <div className="link-item">
                      <h4>Revenue Sharing</h4>
                      <p>Trading fees are distributed to selected charities via 0xSplits</p>
                      <div className="external-link">
                        <ExternalLink size={14} />
                        <a 
                          href={`https://basescan.org/address/${coinData.splitsAddress}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Splits Contract
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="report-area">
                    <div className="report-trigger">
                      <motion.button
                        className="btn btn-secondary btn-small report-btn"
                        onClick={() => setShowReportSection(!showReportSection)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <AlertTriangle size={14} />
                        {showReportSection ? 'Cancel Report' : 'Report Content'}
                      </motion.button>
                      <p className="report-description">
                        Help us maintain a safe community by reporting inappropriate content
                      </p>
                    </div>

                    <AnimatePresence>
                      {showReportSection && (
                        <motion.div
                          className="report-form-container"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                          <div className="testimony-preview">
                            <h4>Reporting confession:</h4>
                            <p>"{coinData.textContent?.substring(0, 100)}{coinData.textContent?.length > 100 ? '...' : ''}"</p>
                          </div>

                          <form onSubmit={handleSubmitReport} className="report-form">
                            <div className="form-group">
                              <label htmlFor="reason">Why are you reporting this content? *</label>
                              <select
                                id="reason"
                                value={reportReason}
                                onChange={(e) => setReportReason(e.target.value)}
                                required
                                disabled={isSubmittingReport}
                                className="report-select"
                              >
                                <option value="">Select a reason...</option>
                                {reportReasons.map((reasonOption) => (
                                  <option key={reasonOption} value={reasonOption}>
                                    {reasonOption}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="form-group">
                              <label htmlFor="details">Additional details (optional)</label>
                              <textarea
                                id="details"
                                value={reportDetails}
                                onChange={(e) => setReportDetails(e.target.value)}
                                placeholder="Provide any additional context that might help our moderation team..."
                                rows={3}
                                maxLength={500}
                                disabled={isSubmittingReport}
                                className="report-textarea"
                              />
                              <div className="character-count">
                                {reportDetails.length}/500
                              </div>
                            </div>

                            <div className="report-actions">
                              <motion.button
                                type="button"
                                className="btn btn-secondary btn-small"
                                onClick={() => {
                                  setShowReportSection(false);
                                  setReportReason('');
                                  setReportDetails('');
                                }}
                                disabled={isSubmittingReport}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                Cancel
                              </motion.button>
                              <motion.button
                                type="submit"
                                className="btn btn-danger btn-small"
                                disabled={isSubmittingReport || !reportReason}
                                whileHover={!isSubmittingReport && reportReason ? { scale: 1.02 } : {}}
                                whileTap={!isSubmittingReport && reportReason ? { scale: 0.98 } : {}}
                              >
                                {isSubmittingReport ? (
                                  <>
                                    <div className="spinner small"></div>
                                    Submitting...
                                  </>
                                ) : (
                                  <>
                                    Submit Report
                                  </>
                                )}
                              </motion.button>
                            </div>
                          </form>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

            </div>
          ) : (
            <div className="modal-error">
              <p>Failed to load coin data</p>
              <button className="btn btn-secondary" onClick={fetchCoinData}>
                Retry
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CoinModal;